import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { Civilization } from '@galaxis/shared';
import { LoginScreen, type AuthLink } from './components/LoginScreen.tsx';
import { TopNav } from './components/TopNav.tsx';
import { ResourceBar } from './components/ResourceBar.tsx';
import { Toaster } from './components/Toaster.tsx';
// The map (PixiJS) is the heavy part of the bundle: the sign-in screen must not wait for it
const loadGalaxyView = () => import('./components/GalaxyView.tsx').then(m => ({ default: m.GalaxyView }));
const GalaxyView = lazy(loadGalaxyView);
import { FleetsView } from './components/FleetsView.tsx';
import { EconomyView } from './components/EconomyView.tsx';
import { DiplomacyView } from './components/DiplomacyView.tsx';
import { ResearchView } from './components/ResearchView.tsx';
import { ConstructionView } from './components/ConstructionView.tsx';
import { TradeView } from './components/TradeView.tsx';
import { CommunityView } from './components/CommunityView.tsx';
import { AccountView } from './components/AccountView.tsx';
import { WelcomeBack } from './components/WelcomeBack.tsx';
import { useGameBootstrap } from './hooks/useGameBootstrap.ts';
import { useGameStore } from './store/game-store.ts';
import { refreshSession, onLoggedOut } from './api/session.ts';
import { fetchMe } from './api/galaxy.ts';
import { FoundingScreen } from './components/founding/FoundingScreen.tsx';
const AdminApp = lazy(() => import('./components/admin/AdminApp.tsx').then(m => ({ default: m.AdminApp })));
import { OnboardingGuide } from './components/founding/OnboardingGuide.tsx';
import { fetchOnboarding } from './api/onboarding.ts';
import { GuestBanner } from './components/GuestBanner.tsx';
import { useOnboardingSync } from './hooks/useOnboardingSync.ts';
import { FeedbackDialog } from './components/FeedbackDialog.tsx';
import { ChangelogPage } from './components/ChangelogPage.tsx';
import { UpdateBanner } from './components/UpdateBanner.tsx';
import { SharedReplayPage, sharedTokenFromPath } from './components/SharedReplayPage.tsx';
import { LegalPage, legalKeyFromPath } from './components/LegalPage.tsx';
import { LegalGate } from './components/LegalGate.tsx';
import { useAudioBridge } from './audio/useAudioBridge.ts';

/** E-mail links (?verify= / ?reset= / M10.6 ?invite=) are read once and removed from the address bar and history. */
function takeAuthLink(): AuthLink | null {
  const q = new URLSearchParams(window.location.search);
  const verify = q.get('verify');
  const reset = q.get('reset');
  const invite = q.get('invite');
  if (!verify && !reset && !invite) return null;
  window.history.replaceState(null, '', window.location.pathname);
  if (verify) return { kind: 'verify', token: verify };
  if (reset) return { kind: 'reset', token: reset };
  return { kind: 'invite', token: invite! };
}
const initialLink = takeAuthLink();

type Phase = 'checking' | 'anon' | 'authed';

export default function App() {
  // M10.8: the public changelog needs no session
  if (window.location.pathname.replace(/\/+$/, '') === '/changelog') return <ChangelogPage />;
  // Legal pages (also the links of every e-mail footer): public, no session
  const legal = legalKeyFromPath(window.location.pathname);
  if (legal) return <LegalPage docKey={legal} />;
  if (/^\/legal(\/|$)/.test(window.location.pathname)) return <LegalPage docKey="privacy" />;
  // Track 2: a shared battle report is public (read-only, no session)
  const shared = sharedTokenFromPath(window.location.pathname);
  if (shared) return <SharedReplayPage token={shared} />;
  return <SessionApp />;
}

function SessionApp() {
  // M10.2: the session is restored from the HttpOnly refresh cookie, not from page storage
  const [phase, setPhase] = useState<Phase>(initialLink ? 'anon' : 'checking');
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    if (phaseRef.current !== 'checking') return;
    let live = true;
    void refreshSession().then(r => { if (live) setPhase(r ? 'authed' : 'anon'); });
    return () => { live = false; };
  }, []);

  // Expired/revoked session while playing: start over from a clean page (clears all game state)
  useEffect(() => onLoggedOut(() => { if (phaseRef.current === 'authed') window.location.replace('/'); }), []);

  useEffect(() => {
    if (phase !== 'anon') return;
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    const t = idle ? idle(() => void loadGalaxyView()) : window.setTimeout(() => void loadGalaxyView(), 1500);
    return () => { if (!idle) clearTimeout(t); };
  }, [phase]);

  if (phase === 'checking') {
    return (
      <div className="w-full h-full flex items-center justify-center bg-space-900 text-slate-400 text-sm" role="status">
        Kapcsolódás…
      </div>
    );
  }
  if (phase === 'anon') return <LoginScreen onLogin={() => setPhase('authed')} link={initialLink} />;
  // M10.5: operations console (the server checks the admin role on every call)
  // Legal pages: a changed Terms / Privacy version (or an account from before them) is accepted before anything else
  if (window.location.pathname.replace(/\/+$/, '') === '/admin') return <LegalGate><Suspense fallback={null}><AdminApp /></Suspense></LegalGate>;
  // M10.8: the new-version notice covers every signed-in state (founding screen, game)
  return <><LegalGate><AuthedRoot /></LegalGate><UpdateBanner /></>;
}

/** M10.3: an account without a civilization founds one first; with one, the game opens directly. */
function AuthedRoot() {
  const [civ, setCiv] = useState<Civilization | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchMe().then(me => {
      if (!live) return;
      setCiv(me.civilization);
      // M10.4: the guide opens by itself until it is completed or skipped (server-side state)
      if (me.civilization) fetchOnboarding().then(o => { useGameStore.getState().setOnboarding(o); useGameStore.getState().setGuideOpen(o.autoOpen);
        // Fresh civilization (e.g. guest quick start): open the map on the home system
        const home = me.civilization!.homeSystemId;
        if (o.autoOpen && !o.steps[0]?.done) useGameStore.setState({ activeTab: 'galaxy', selectedSystemId: home, mapFocusSystemId: home });
      }).catch(() => undefined);
    }).catch(e => { if (live) setError(e instanceof Error ? e.message : 'Betöltési hiba'); });
    return () => { live = false; };
  }, []);

  const onFounded = useCallback((c: Civilization) => {
    // Open the map on the new home system
    useGameStore.setState({ activeTab: 'galaxy', selectedSystemId: c.homeSystemId, mapFocusSystemId: c.homeSystemId });
    useGameStore.getState().setGuideOpen(true);
    setCiv(c);
  }, []);

  if (error) {
    return <div className="w-full h-full flex items-center justify-center p-6 text-sm notice notice-error" role="alert">{error}</div>;
  }
  if (civ === undefined) {
    return <div className="w-full h-full flex items-center justify-center bg-space-900 text-slate-400 text-sm" role="status">Betöltés…</div>;
  }
  if (civ === null) return <FoundingScreen onFounded={onFounded} />;
  return <AuthedApp civ={civ} />;
}

function AuthedApp({ civ }: { civ: Civilization }) {
  const { activeTab, guideOpen, feedbackOpen } = useGameStore();
  useGameBootstrap();
  useOnboardingSync(civ.homeSystemId);

  useAudioBridge();

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <TopNav />
      <ResourceBar />
      <GuestBanner />
      <Toaster />
      {/* The welcome-back summary is for returning players, the guide for brand-new ones */}
      {guideOpen ? <OnboardingGuide homeSystemId={civ.homeSystemId} /> : <WelcomeBack />}
      {feedbackOpen && <FeedbackDialog />}
      <main className="flex-1 relative overflow-hidden flex flex-col">
        {activeTab === 'galaxy' && <Suspense fallback={<div className="flex-1 flex items-center justify-center text-slate-400 text-sm" role="status">Térkép betöltése…</div>}><GalaxyView /></Suspense>}
        {activeTab === 'fleets' && <FleetsView />}
        {activeTab === 'empire' && <EconomyView />}
        {activeTab === 'diplomacy' && <DiplomacyView />}
        {activeTab === 'research' && <ResearchView />}
        {activeTab === 'construction' && <ConstructionView />}
        {activeTab === 'trade' && <TradeView />}
        {activeTab === 'community' && <CommunityView />}
        {activeTab === 'account' && <AccountView />}
      </main>
    </div>
  );
}
